import { useEffect, useState } from 'react';
import { getStoredPreviewDevice, resolvePreviewOverrideBreakpoint } from '@/hooks/usePreviewDevice';

export type DashboardBreakpoint = 'mobile' | 'tablet' | 'desktop';

const getBreakpointFromWidth = (width: number): DashboardBreakpoint => {
  // Tailwind defaults: md=768, lg=1024
  if (width >= 1024) return 'desktop';
  if (width >= 768) return 'tablet';
  return 'mobile';
};

export const useDashboardBreakpoint = (): DashboardBreakpoint => {
  const [breakpoint, setBreakpoint] = useState<DashboardBreakpoint>(() => {
    if (typeof window === 'undefined') return 'desktop';

    const override = resolvePreviewOverrideBreakpoint(getStoredPreviewDevice());
    if (override) return override;
    return getBreakpointFromWidth(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId: number | null = null;
    const recompute = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const override = resolvePreviewOverrideBreakpoint(getStoredPreviewDevice());
        if (override) {
          setBreakpoint(override);
          return;
        }
        setBreakpoint(getBreakpointFromWidth(window.innerWidth));
      });
    };

    window.addEventListener('resize', recompute, { passive: true });
    window.addEventListener('preview-device-changed', recompute as EventListener);
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('preview-device-changed', recompute as EventListener);
    };
  }, []);

  return breakpoint;
};

export const resolveDashboardLayoutStorageKey = (baseKey: string, breakpoint: DashboardBreakpoint): string => {
  return `${baseKey}:${breakpoint}`;
};
